import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService }        from '@ngx-translate/core';

import { ImportFailureGroupModel } from './typings/imports.summary';
import { ImportRunSummaryModel }   from './typings/imports.summary';
import { ImportsService }          from './imports.service';
import { MessagesService }         from '../messages/messages.service';

// WS-6752. Between confirming the mapping and importing, show the operator what is
// about to happen. Until now they pressed import and found out afterwards, by which
// point part of the file had been written and then partly destroyed.
//
// Everything shown here comes from the check that runs before anything is written, so
// reaching this screen has cost nothing and going back from it costs nothing either.
@Component({
  selector   : 'wor-import-summary',
  styles: [ require('./import.summary.component.scss') ],
  template: require('./import.summary.component.html')
})
export class ImportSummaryComponent implements OnInit {
  // Rows in the operator's file, counted by the screen that parsed it. The backend
  // reports what will not import; how many that leaves is only knowable with the total.
  @Input() totalRows = 0;

  @Output() back      = new EventEmitter<void>();
  @Output() proceeded = new EventEmitter<void>();

  summary   : ImportRunSummaryModel;
  decisions : { [ answerKey : string ] : string } = {};

  loading    = false;
  submitting = false;

  // WS-6819. True only while the file is being fetched, so the button cannot be pressed
  // twice and produce two downloads of the same rows.
  downloading = false;

  constructor (
    protected importsService  : ImportsService,
    protected messagesService : MessagesService,
    protected translateService: TranslateService
  ) {}

  // WS-6807. Nothing else fetches this. The page around this component shows it with an
  // *ngIf, so mounting is the only moment it can load, and its own template is hidden
  // until the summary arrives. Without this it was shown, fetched nothing, and drew
  // nothing, which is why no held back row could ever be answered and why the whole
  // resolution step was unreachable from the moment it was built.
  //
  // Being tied to mounting is also what makes a second round work. The page hides this
  // while the run is moving, so a run that parks again unmounts and remounts, and loads
  // again on its own.
  ngOnInit () : void {
    this.refresh();
  }

  get groups () : Array<ImportFailureGroupModel> {
    return this.summary?.groups || [];
  }

  get hasProblems () : boolean {
    return this.groups.length > 0;
  }

  get isAwaitingDecisions () : boolean {
    return this.summary?.status === 'awaiting_decisions';
  }

  get isFinished () : boolean {
    return this.summary?.status === 'finished';
  }

  // What this group's answer is recorded against. Taken from the backend, which sends it
  // per group. The fallback keeps this screen working against a backend that has not
  // shipped WS-6802 yet, where the only groups with anything to answer are the ones
  // answered per reference type.
  answerKeyFor ( group : ImportFailureGroupModel ) : string {
    return group.answer_key || group.reference_type;
  }

  // WS-6802. Whether there is anything to say about this group at all. A group that can
  // neither be created nor imported without its unusable values is described and left
  // alone, because an answer that cannot help is worse than no answer.
  canAnswer ( group : ImportFailureGroupModel ) : boolean {
    return group.can_create || group.can_import_without;
  }

  // Groups the operator could answer but has not. Proceeding while any remain would
  // leave the run parked, so the button says what is still needed rather than appearing
  // to do nothing.
  get unansweredGroups () : Array<ImportFailureGroupModel> {
    return this.groups.filter(group => this.canAnswer(group) && !this.decisions[ this.answerKeyFor(group) ]);
  }

  get canProceed () : boolean {
    return !this.submitting && this.unansweredGroups.length === 0;
  }

  // Held back given the answers so far. A group the operator has chosen to create is no
  // longer held: those rows import once the missing records exist. Nor is a group they
  // have chosen to import without the value that was wrong: those rows come in short of
  // it, which is still coming in.
  get heldBackCount () : number {
    return this.groups.reduce(
      ( total, group ) =>
        total + ( this.willCreate(group) || this.willImportWithout(group) ? 0 : group.count ), 0);
  }

  get willImportCount () : number {
    return Math.max(this.totalRows - this.heldBackCount, 0);
  }

  // WS-6785. The same arithmetic read in the past tense. Kept as its own name because a
  // screen reporting what happened should not be quoting a number called "will".
  get importedCount () : number {
    return this.willImportCount;
  }

  willCreate ( group : ImportFailureGroupModel ) : boolean {
    return group.can_create && this.decisions[ this.answerKeyFor(group) ] === 'create';
  }

  // WS-6802. The whole point of the ticket: these rows come in, with the value that could
  // not be used left empty and everything else intact.
  willImportWithout ( group : ImportFailureGroupModel ) : boolean {
    return group.can_import_without && this.decisions[ this.answerKeyFor(group) ] === 'import_without';
  }

  willSkip ( group : ImportFailureGroupModel ) : boolean {
    return this.canAnswer(group) && this.decisions[ this.answerKeyFor(group) ] === 'skip';
  }

  // Which fields go in empty, ready to read. Listed rather than described, because "and
  // the phone number as well" is the difference between accepting this and going back to
  // fix the file.
  fieldsLeftOut ( group : ImportFailureGroupModel ) : string {
    return ( group.import_without_fields || [] ).join(', ');
  }

  choose ( group : ImportFailureGroupModel, answer : string ) : void {
    if ( !this.canAnswer(group) ) {
      return;
    }

    this.decisions[ this.answerKeyFor(group) ] = answer;
  }

  // WS-6819. Hands back the rows the import could not take, as a file the operator can
  // correct and drop straight back in. The whole recovery loop is this one button plus
  // the reasons already on screen above it.
  //
  // Fetched rather than linked. A plain link would leave the application's session
  // behind and the endpoint would refuse it, and window.open on a blob loses the
  // filename, which matters here: the operator ends up with several of these in a
  // downloads folder and needs to know which import each came from.
  downloadHeldRows () : Promise<void> {
    if ( this.downloading ) {
      return Promise.resolve();
    }

    this.downloading = true;

    return this.importsService.fetchHeldRows()
    .then(({ blob, filename }) => {
      const url  = URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href     = url;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Released on the next tick rather than immediately: revoking it before the click
      // has been serviced cancels the download on some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    })
    .catch(() => {
      this.messagesService.error(
        this.translateService.instant('IMPORT_SPACE.SUMMARY_DOWNLOAD_FAILED'));
    })
    .finally(() => {
      this.downloading = false;
    });
  }

  refresh () : Promise<void> {
    this.loading = true;

    return this.importsService.fetchRunSummary()
    .then(summary => {
      this.summary = summary;
    })
    .finally(() => {
      this.loading = false;
    });
  }

  goBack () : void {
    this.back.emit();
  }

  proceed () : Promise<void> {
    if ( !this.canProceed ) {
      return Promise.resolve();
    }

    this.submitting = true;

    // Only groups with something to answer are sent. The rest have nothing to answer, and
    // sending them would invent an answer the operator never gave.
    const answers = this.groups
      .filter(group => this.canAnswer(group) && this.decisions[ this.answerKeyFor(group) ])
      .reduce(( sent, group ) => {
        sent[ this.answerKeyFor(group) ] = this.decisions[ this.answerKeyFor(group) ];

        return sent;
      }, {} as { [ answerKey : string ] : string });

    return this.importsService.applyImportDecisions(answers)
    .then(() => {
      this.proceeded.emit();
    })
    .finally(() => {
      this.submitting = false;
    });
  }
}
